/*

	messagesByChannelId

contrary to its name, right now it only contains messages for the active channel, 
for other channels it just stores an empty array. 
this is temporary, and will be fixed later. 

v1 was:
* we store each new messages received via real-time socket updates into messagesByChannelId. 
* on changing active channel, we did `shouldFetch = messagesByChannelId[newActiveChannelId].count < pageSize`. 
* the reason why the condition is not `...count == 0` is because of new messages received via socket. consider: 
* active channel is ChannelA, we receive messages in ChannelB, we add them to local store. 
* we switch to ChannelB => local store is _not_ empty, but we only have ChannelB messages received after app launch, 
  not older ChannelB messages. 
* if we have less than a page of messages, then we should fetch. 
* if we have at least a full page, then the "show more' button should be visible, and the user pressing it will fetch messages. 

this was buggy in two ways: 
* "show more" button not shown: 
  * if we have more than a pageful of messages when switching to ChannelB, we didn't actually show the "show more button. 
  * this is because we had no way to know that there are more messages on the backend. 
  * the user ended up seeing only new messages received since app start, with no way to fetch older. 
* also didn't handle app backgrounding. 
  * ChannelA is active, we have a pageful of messages, send app to background. 
  * new messages received in ChannelA on the backend. app is in bg, so it doesn't receive the new messages via socket. 
  * foreground the app => we have a pageful of messages, so we don't fetch. 
  * we only see old messages, but not the new ones sent to ChannelA (by other users) while the app was in background. 

v2 added fetch on foreground: 
* on foregrounding the app: 
  * fetch (first page of) messages for the active channel. 
  * invalidate the cache for all non-active channels. 
* this tried to combine aggressive re-fetching on app foregrounding 
  with keeping messages for all channels in cache while the app is in foreground. 

still had issues: 
* missed messages: 
  * foreground the app, ChannelA is active => fetch ChannelA, clear cache for others
  * while ChannelA is active, receive a pageful of messages in ChannelB via socket. 
  * switch to ChannelB => we have a page of message, so we don't fetch. 
  * we only show new messages, don't fetch older, and don't show "show more" button either. 
  * (this is the same issue mentioned with v1, but v2 didn't solve it either.)
* too many reloads, bad ux: 
  * eg. use image viewer, then the open-in-new-tab button 
    => once back, this is a foregrounding event, so we re-fetch the active channel. 
  * on re-fetch, we clear the cache and only get one page, user needs to press "load more" again 
    to get all the messages they could see before using the image viewer. 

v3 (current): 
* like v2, but we only save messages into messagesByChannelId for the active channel. 
* for non-active channels, on receiving a new message via socket we only update hasUnreadMessages. 
 
this works (?), but kind bad for both ux and performance: 
* has the issue mentioned in v2 (image viewer, open new tab, back => channel reloads, only one page)
* even if in foreground all the time, switching channels will keep loading the last page. 

note most of the issues arise from the case when we receive a new message via socket, 
but we don't have an up-to-date local messages store for that channel. 
* if we simply add the new message to the frontend cache, there might be a gap between the new message the latest-in-cache. 
* on the frontend there might be messages in between, added while the frontend app was suspended. 
* also, there are actually two cases when we might receive a new message, 
  and what backend things newMessage.lastMessage is, is not the same as what frontend has as lates message in that channel: 
  1. close-in-time messages from multiple users, received out-of-order 
     (backend stores MsgA first, then MsgB, but frontend receives MsgB first). 
  2. app didn't receive some messages while it was suspended. 

maybe we could try something this (potential v4?): 
* backend: 
  * when finished writing a new message to db, we read back the previous message (ulid < newMessage.ulid, limit = 1). 
  * the backend already sends back an enriched version of the message to the frontend (with the ulid added), 
    now we'll also send the prevMessageUlid, to feathers-server, and then to the frontend. 
  * because of how we read the prev msg ulid with `ulid < newMessage.ulid, limit = 1`, 
    i _think_ there's no race condition when writing two messages at the same time. 
    dynamodb serializes writes, and this produces a strict ordering of messages. 	
* the frontend store stores not only a single array of messages per channel, but multiple arrays / ranges of messages. 
  * within each range, messages are contiguous (no gaps): `msg[n + 1].prevMsgUlid == msg[n].ulid`. 
  * (since this is guaranteed, we don't necessarily have to store prevMsgUlid for each message, 
    but we do need prevMsgUlid of the first (oldest) message in each range. 
* when frontend receives a message via socket, we store it. 
  * as messages within a range are contiguous, we know that the new message will not be inserted there. 
  * either it will be added at the end of an existing range, or open a new range. 
  * iterate ranges backwards (newest to oldest), compare newMsg.prevMsgUlid and range.lastMsg.ulid. 
  * if newMsg.prevMsgUlid == range[r].lastMsg.ulid: 
    * add the new msg to range r. 
    * if range[r + 1].firstMsg.prevMsgUlid == newMsg.ulid then merge ranges r and r + 1. 
      * (eg. this happens when receiving message out of order: backend has MsgA, MsgB, frontend receives MsgB first: 
        MsgB will open a new range, then MsgA will merge the two ranges.)
  * if range[r].lastMsg.ulid < newMsg.ulid, but range[r].lastMsg.ulid != newMsg.prevMsgUlid: 
    * we should insert the new message after the last message in range r, 
      _BUT_ there's no continuity between these two messages 
      (in db the new message does not immediately follow range r's last msg). 
    * open a new range, with a single message newMessage. 
    * this can mean one of two things: 
      a. messages arriving out of order. 
        * last message was ML, then users A and B sent MA and MB, close in time. 
        * in db we have ML, MA, MB, but frontend receives MB first. 
        * last range last msg is ML, but MB.prevMsgUlid != ML.ulid => open a new range. 
        * later we'll receive MA, and merge the new range (with only MB in it) with the prev range (where ML is the last msg). 
      b. the has been suspended for some time. 
        * the new range will keep growing, then at some later point we might fill in the gap. 
        * but we don't fill the gap when receiving messages via socket, only when the channel becomes active.
* we try to fill the gaps only when/if the channel becomes active. 
  * we limit the number of messages we insert in one gap-filling attempt. 
  * once done, if we still have multiple ranges, we only keep the last one, throw away the others 
    (and set hasMoreMessage = true, so the "load more" button will be shown). 
* open questions: 
  * cache eviction? how do we make sure our cache doesn't grow without limit? 
  * what if we receive new messages via socket while the gap-filling algorithm is working? 
    (gap-filling involves async steps.)

*/

import { create } from 'zustand'

import { Message } from '../../../shared-src/message'
import { Channel } from '../../../shared-src/channel'
import { PaginatedMessagesResponse } from '../../../shared-src/paginated-messages'

type ChatState = { 
	channels: Channel[]
	activeChannelId: string | null
	activeChannel: Channel | null
	messagesByChannelId: Record<string, Message[]>
	loadingMoreByChannelId: Record<string, boolean>
	hasMoreMessagesByChannelId: Record<string, boolean>
	unreadChannelIds: Set<string>
	pageSize: number

	setChannels: (channels: Channel[]) => void
	addChannel: (channel: Channel) => void
	updateChannel: (channel: Channel) => void
	removeChannel: (channelId: string) => void
	setActiveChannelId: (channelId: string) => void
	addMessage: (message: Message) => void
	updateMessage: (message: Message) => void
	setMessagesForChannel: (channelId: string, response: PaginatedMessagesResponse) => void
	prependMessages: (channelId: string, response: PaginatedMessagesResponse) => void
	setLoadingMore: (channelId: string, loading: boolean) => void
	markChannelAsRead: (channelId: string) => void
	invalidateInactiveChannelsMessages: (activeChannelId: string) => void
	clearChat: () => void
}

export const useChatStore = create<ChatState>((set, get) => ({
	channels: [],
	activeChannelId: null,
	activeChannel: null, 
	messagesByChannelId: {},
	loadingMoreByChannelId: {},
	hasMoreMessagesByChannelId: {},
	unreadChannelIds: new Set(),
	pageSize: 10,

	setChannels: (channels) => {
		const unreadChannelIds = new Set<string>()
		
		channels.forEach(channel => {
			if (channel.hasUnreadMessages) {
				unreadChannelIds.add(channel.id)
			}
		})
		
		get().unreadChannelIds.forEach(channelId => {
			unreadChannelIds.add(channelId)
		})
		
		set({ 
			channels,
			unreadChannelIds
		})
	},

	addChannel: (channel) => set((state) => { 
		return { 
			channels: [...state.channels, channel], 
			unreadChannelIds: new Set([...state.unreadChannelIds, channel.id])
		}
	}),

	updateChannel: (channel) => set((state) => ({
		channels: state.channels.map(c => c.id === channel.id ? channel : c),
		activeChannel: state.activeChannelId === channel.id ? channel : state.activeChannel
	})),

	removeChannel: (channelId) => set((state) => {
		const newUnreadChannelIds = new Set(state.unreadChannelIds)
		newUnreadChannelIds.delete(channelId)

		const newMessagesByChannelId = { ...state.messagesByChannelId }
		delete newMessagesByChannelId[channelId]

		const newLoadingMoreByChannelId = { ...state.loadingMoreByChannelId }
		delete newLoadingMoreByChannelId[channelId]

		const newHasMoreMessagesByChannelId = { ...state.hasMoreMessagesByChannelId }
		delete newHasMoreMessagesByChannelId[channelId]

		const filteredChannels = state.channels.filter(c => c.id !== channelId)
		const wasActiveChannel = state.activeChannelId === channelId

		return {
			channels: filteredChannels,
			activeChannelId: wasActiveChannel && filteredChannels.length > 0 ? filteredChannels[0].id : (wasActiveChannel ? null : state.activeChannelId),
			activeChannel: wasActiveChannel && filteredChannels.length > 0 ? filteredChannels[0] : (wasActiveChannel ? null : state.activeChannel),
			messagesByChannelId: newMessagesByChannelId,
			loadingMoreByChannelId: newLoadingMoreByChannelId,
			hasMoreMessagesByChannelId: newHasMoreMessagesByChannelId,
			unreadChannelIds: newUnreadChannelIds
		}
	}),

	setActiveChannelId: (channelId) => {
		set({ 
			activeChannelId: channelId,
			activeChannel: get().channels.find(channel => channel.id === channelId) || null
		})
		get().markChannelAsRead(channelId)
	},

	addMessage: (message: Message) => set((state) => {
		const channelId = message.channelId

		if (!state.channels.find(channel => channel.id === channelId)) {
			console.log("addMessage: channel not found", channelId)
			return state
		}

		const newUnreadChannelIds = new Set(state.unreadChannelIds)

		// temporary workaround: don't store messages for non-active channels. 
		// work around the case when a non-active channel accumulates a pageful of message via socket updates, 
		// then on becoming active we wouldn't fetch messages and wouldn't show a "load more" button either. 
		if (channelId !== state.activeChannelId) {
			newUnreadChannelIds.add(channelId)
			return { unreadChannelIds: newUnreadChannelIds }
		}

		const messages = state.messagesByChannelId[channelId] || []
		return {
			messagesByChannelId: {
				...state.messagesByChannelId,
				[channelId]: [...messages, message]
			},
			unreadChannelIds: newUnreadChannelIds
		}
	}),

	updateMessage: (message: Message) => set((state) => {
		const channelId = message.channelId

		if (!state.channels.find(channel => channel.id === channelId)) {
			console.log("updateMessage: channel not found", channelId)
			return state
		}

		const messages = state.messagesByChannelId[channelId] || []
		// Explicitly include reactions so that clearing reactions (reactions=undefined, absent from
		// JSON-deserialized message) overwrites the old value rather than being silently skipped by spread.
		const updatedMessages = messages.map(m => m.ulid === message.ulid ? { ...m, ...message, reactions: message.reactions } : m)

		return {
			messagesByChannelId: {
				...state.messagesByChannelId,
				[channelId]: updatedMessages
			}
		}
	}), 

	setMessagesForChannel: (channelId: string, response: PaginatedMessagesResponse) => set((state) => ({
		messagesByChannelId: {
			...state.messagesByChannelId,
			[channelId]: response.messages
		},
		hasMoreMessagesByChannelId: {
			...state.hasMoreMessagesByChannelId,
			[channelId]: response.hasMore
		}
	})), 

	prependMessages: (channelId: string, response: PaginatedMessagesResponse) => set((state) => {
		const existingMessages = state.messagesByChannelId[channelId] || []
		return {
			messagesByChannelId: {
				...state.messagesByChannelId,
				[channelId]: [...response.messages, ...existingMessages]
			},
			hasMoreMessagesByChannelId: {
				...state.hasMoreMessagesByChannelId,
				[channelId]: response.hasMore
			}
		}
	}),

	setLoadingMore: (channelId: string, loading: boolean) => set((state) => ({
		loadingMoreByChannelId: {
			...state.loadingMoreByChannelId,
			[channelId]: loading
		}
	})),

	markChannelAsRead: (channelId: string) => set((state) => {
		const newUnreadChannelIds = new Set(state.unreadChannelIds)
		newUnreadChannelIds.delete(channelId)
		return { unreadChannelIds: newUnreadChannelIds }
	}),

	invalidateInactiveChannelsMessages: (activeChannelId: string) => set((state) => {
		const newMessagesByChannelId = {
			[activeChannelId]: state.messagesByChannelId[activeChannelId] || []
		}
		const newLoadingMoreByChannelId = {
			[activeChannelId]: state.loadingMoreByChannelId[activeChannelId] || false
		}
		const newHasMoreMessagesByChannelId = {
			[activeChannelId]: state.hasMoreMessagesByChannelId[activeChannelId] || false
		}
		return {
			messagesByChannelId: newMessagesByChannelId,
			loadingMoreByChannelId: newLoadingMoreByChannelId,
			hasMoreMessagesByChannelId: newHasMoreMessagesByChannelId
		}
	}),

	clearChat: () => set({
		channels: [],
		activeChannelId: null,
		activeChannel: null,
		messagesByChannelId: {},
		loadingMoreByChannelId: {},
		hasMoreMessagesByChannelId: {},
		unreadChannelIds: new Set()
	}),
}))
